import React, { useCallback, useState } from 'react';

import { clearCachedToken } from 'api/authClient';
import { getWorkflow } from 'api/workflowsClient';
import { setRuntimeConfig } from 'config/runtimeConfig';
import type { AuthConfig } from 'model/authConfig';
import { getActiveConfig } from 'model/authConfig';
import type { WorkflowDto } from 'model/workflow';
import { SPECIAL_ACTIONS } from 'model/specialActions';
import { clearCachedGraph } from 'utils/workflowCache';
import { AuthConfigManager } from 'components/auth/AuthConfigManager';
import { ErrorMessage } from 'components/common/ErrorMessage';
import Styled from './WorkflowSelectorPage.styled';

const {
  StyledShell,
  StyledMain,
  StyledContent,
  StyledHeaderBlock,
  StyledTitle,
  StyledSubtitle,
  StyledCard,
  StyledCardSection,
  StyledCardSectionWithDivider,
  StyledSectionTitle,
  StyledActiveEnvInfo,
  StyledActiveEnvName,
  StyledActiveEnvUrl,
  StyledLoadRow,
  StyledWorkflowIdInput,
  StyledCreateNewSlot,
  StyledCreateNewButton,
  StyledErrorSlot,
} = Styled;

interface OwnProps {
  onWorkflowLoaded: (workflowId: string, workflow: WorkflowDto) => void;
}

/**
 * Landing page: pick an auth-config environment and either load a saved
 * workflow by id or start a fresh one (Initial + Output skeleton). Owns
 * just the small bits of state that drive the form; the AuthConfigManager
 * owns its own list/edit state.
 */
const WorkflowSelectorPage = ({ onWorkflowLoaded }: OwnProps) => {
  const [workflowId, setWorkflowId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentConfig, setCurrentConfig] = useState<AuthConfig | null>(getActiveConfig());

  const handleConfigSelected = useCallback((config: AuthConfig) => {
    setCurrentConfig(config);
    setRuntimeConfig({
      apiBaseUrl: config.apiBaseUrl,
      clientId: config.clientId,
      clientSecret: config.clientSecret,
    });
    clearCachedToken();
    setError(null);
  }, []);

  const handleChangeWorkflowId = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkflowId(event.target.value);
  }, []);

  const handleLoadWorkflow = useCallback(async () => {
    const trimmedId = workflowId.trim();
    if (!trimmedId) return;

    if (!currentConfig) {
      setError('Please configure an environment first');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const workflow = await getWorkflow(trimmedId);
      onWorkflowLoaded(trimmedId, workflow);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load workflow';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [currentConfig, onWorkflowLoaded, workflowId]);

  const handleCreateNew = useCallback(() => {
    const newWorkflow: WorkflowDto = {
      name: 'New Workflow',
      steps: {
        Initial: {
          id: 'Initial',
          actionId: SPECIAL_ACTIONS.initial,
          input: {},
          settings: {},
          runAfter: [],
        },
        Output: {
          id: 'Output',
          actionId: SPECIAL_ACTIONS.output,
          input: {},
          settings: {},
          runAfter: [{ stepId: 'Initial' }],
        },
      },
    };
    // The 'new' cache key is shared across every fresh workflow; clear it so
    // the editor doesn't restore the previous in-progress draft.
    clearCachedGraph('new');
    onWorkflowLoaded('new', newWorkflow);
  }, [onWorkflowLoaded]);

  const handleSubmit = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      void handleLoadWorkflow();
    },
    [handleLoadWorkflow],
  );

  return (
    <StyledShell>
      <header className='app-navbar'>
        <div className='brand'>
          <img src='/assets/logo-white.svg' alt='Hyarchis' />
          <span className='product'>WORKFLOW BUILDER</span>
        </div>
        <div className='nav-context' />
      </header>
      <StyledMain>
        <StyledContent>
          <StyledHeaderBlock>
            <StyledTitle>Workflow Builder</StyledTitle>
            <StyledSubtitle>
              Configure your environment and load a workflow to get started.
            </StyledSubtitle>
          </StyledHeaderBlock>

          <StyledCard>
            <StyledCardSectionWithDivider>
              <AuthConfigManager onConfigSelected={handleConfigSelected} />
            </StyledCardSectionWithDivider>

            <StyledCardSection>
              <StyledSectionTitle>Load Workflow</StyledSectionTitle>

              {currentConfig && (
                <StyledActiveEnvInfo>
                  Active environment:{' '}
                  <StyledActiveEnvName>{currentConfig.name}</StyledActiveEnvName>
                  <br />
                  <StyledActiveEnvUrl>{currentConfig.apiBaseUrl}</StyledActiveEnvUrl>
                </StyledActiveEnvInfo>
              )}

              <form onSubmit={handleSubmit}>
                <StyledLoadRow>
                  <StyledWorkflowIdInput
                    type='text'
                    value={workflowId}
                    onChange={handleChangeWorkflowId}
                    placeholder='Enter workflow ID'
                    disabled={!currentConfig}
                    aria-label='Workflow ID'
                  />
                  <button
                    type='submit'
                    disabled={!workflowId.trim() || isLoading || !currentConfig}
                  >
                    {isLoading ? 'Loading…' : 'Load Workflow'}
                  </button>
                </StyledLoadRow>
              </form>

              {currentConfig && (
                <StyledCreateNewSlot>
                  <StyledCreateNewButton
                    type='button'
                    onClick={handleCreateNew}
                    className='btn-secondary'
                  >
                    Create New Workflow
                  </StyledCreateNewButton>
                </StyledCreateNewSlot>
              )}

              {error && (
                <StyledErrorSlot>
                  <ErrorMessage variant='banner'>{error}</ErrorMessage>
                </StyledErrorSlot>
              )}
            </StyledCardSection>
          </StyledCard>
        </StyledContent>
      </StyledMain>
    </StyledShell>
  );
};

export default React.memo(WorkflowSelectorPage);
export { WorkflowSelectorPage };
